import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { displaySuccessMessage, displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Stack,
  Collapse,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import EmailIcon from '@mui/icons-material/Email';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterListIcon from '@mui/icons-material/FilterList';
import BusinessIcon from '@mui/icons-material/Business';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getComparator, stableSort } from 'libs/sortList';
import { getClientNotificationEmail } from 'api/activityReport';
import { SendNotification } from 'api/canmail/CANmailAPI';
import { getClientBranding } from 'utils/clientBranding';
import { generateActivityReportEmail, determineTemplateType } from 'utils/emailTemplates/activityReportTemplate';

import { statusConfig, headCells, formatDateTime } from './patrolUtils';
import usePatrolActions from './usePatrolActions';
import useFileActions from './useFileActions';
import PatrolExpandedRow from './PatrolExpandedRow';
import PatrolEditModal from './PatrolEditModal';
import { FilterDialog, EmailDialog, FileViewerDialog, DeleteConfirmDialog, UploadDialog } from './PatrolDialogs';

const PatrolTable = ({ patrols = [], customerId, isLoading = false, onRefetch, startDate, endDate, customerName, locationName, patrolStats = {}, readOnly = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('startTime');

  // Filter state
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterIncidents, setFilterIncidents] = useState(true);
  const [filterPatrols, setFilterPatrols] = useState(true);

  // Email state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailChips, setEmailChips] = useState([]);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);

  const patrolActions = usePatrolActions({ onRefetch });

  const fileActions = useFileActions({
    onRefetch,
    customerName,
    locationName,
    selectedPatrol: patrolActions.selectedPatrol,
    setSelectedPatrol: patrolActions.setSelectedPatrol,
    setExpandedPatrolsData: patrolActions.setExpandedPatrolsData,
    expandedPatrolIds: patrolActions.expandedPatrolIds,
    setExpandedPatrolIds: patrolActions.setExpandedPatrolIds,
  });

  const handleSort = (property) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredPatrols = useMemo(() => {
    return patrols.filter((patrol) => {
      if (filterIncidents && filterPatrols) return true;
      if (filterIncidents && patrol.hasIncident) return true;
      if (filterPatrols && !patrol.hasIncident) return true;
      return false;
    });
  }, [patrols, filterIncidents, filterPatrols]);

  const sortedPatrols = useMemo(
    () => stableSort(filteredPatrols, getComparator(order, orderBy)),
    [filteredPatrols, order, orderBy]
  );

  const paginatedPatrols = useMemo(
    () => sortedPatrols.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedPatrols, page, rowsPerPage]
  );

  // Email handlers
  const handleOpenEmailModal = async () => {
    setEmailOpen(true);
    setIsLoadingEmails(true);
    try {
      if (customerId) {
        const emails = await getClientNotificationEmail(customerId);
        if (emails && emails.length > 0) setEmailChips(emails);
      }
    } catch (error) {
      console.error('Failed to fetch client notification emails:', error);
    } finally {
      setIsLoadingEmails(false);
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' && emailInput.trim()) {
      e.preventDefault();
      if (!emailChips.includes(emailInput.trim())) {
        setEmailChips([...emailChips, emailInput.trim()]);
      }
      setEmailInput('');
    }
  };

  const handleSendEmail = async () => {
    if (emailChips.length === 0) return;
    setIsSendingEmail(true);

    try {
      const branding = getClientBranding();
      const templateType = determineTemplateType(startDate, endDate, patrolStats);
      const reportUrl = `${window.location.origin}/canappsv3/activity-report`;

      const emailHtml = generateActivityReportEmail({
        branding,
        templateType,
        locationName: locationName || 'N/A',
        startDate: startDate || 'N/A',
        endDate: endDate || 'N/A',
        stats: {
          totalPatrols: patrolStats.patrolCount || 0,
          clearCount: patrolStats.clearCount || 0,
          warningCount: patrolStats.warningCount || 0,
          emergencyCount: patrolStats.emergencyCount || 0,
          incompleteCount: patrolStats.incompleteCount || 0,
        },
        reportUrl,
      });

      const sendPromises = emailChips.map((recipientEmail) =>
        SendNotification(recipientEmail, branding.fromEmail, branding.fromEmail, branding.emailSubject, emailHtml)
      );

      await Promise.all(sendPromises);
      dispatch(displaySuccessMessage(`Email sent successfully to ${emailChips.length} recipient(s)!`));
      setEmailOpen(false);
      setEmailChips([]);
    } catch (error) {
      console.error('Failed to send email:', error);
      dispatch(displayErrorMessage('Failed to send email. Please try again.'));
    } finally {
      setIsSendingEmail(false);
    }
  };

  // PDF export
  const handleExportPdf = () => {
    const columns = ['Patrol ID', 'Start Time', 'End Time', 'Guard Name', 'Patrol Type', 'Status', 'Comment'];
    const tableData = sortedPatrols.map((patrol) => [
      patrol.id,
      formatDateTime(patrol.startTime),
      formatDateTime(patrol.endTime),
      patrol.guardName,
      patrol.patrolType,
      statusConfig[patrol.status]?.label || patrol.status,
      patrol.comment || '',
    ]);

    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text('Patrol Log Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Client: ${customerName || 'N/A'}`, 14, 23);
    doc.text(`Location: ${locationName || 'N/A'}`, 14, 29);
    doc.text(`Between ${startDate || 'N/A'} and ${endDate || 'N/A'}`, 14, 35);
    doc.text(`Total Patrols: ${sortedPatrols.length}`, 14, 41);

    doc.autoTable({
      head: [columns],
      body: tableData,
      startY: 48,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [25, 118, 210] },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 22 },
        5: { cellWidth: 22 },
        6: { cellWidth: 'auto' },
      },
    });

    doc.save('patrol-log-report.pdf');
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Patrol Log
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Filter">
              <IconButton size="small" onClick={() => setFilterOpen(true)}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to PDF">
              <IconButton size="small" color="error" onClick={handleExportPdf}>
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Email Report">
              <IconButton size="small" color="primary" onClick={handleOpenEmailModal}>
                <EmailIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {headCells.map((cell) => {
                  const isActive = orderBy === cell.property;
                  const direction = isActive ? order : 'asc';

                  return (
                    <TableCell
                      key={cell.id}
                      sortDirection={isActive ? order : false}
                      sx={{ fontWeight: 600, width: cell.width }}
                    >
                      {cell.sortable ? (
                        <TableSortLabel
                          active={isActive}
                          direction={direction}
                          onClick={handleSort(cell.property)}
                        >
                          {cell.label}
                        </TableSortLabel>
                      ) : (
                        cell.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPatrols.map((patrol) => {
                const status = statusConfig[patrol.status];
                const isExpanded = patrolActions.expandedPatrolIds.has(patrol.id);
                const patrolData = patrolActions.expandedPatrolsData[patrol.id] || patrol;

                return (
                  <React.Fragment key={patrol.id}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : 'inherit',
                        '& > *': { borderBottom: isExpanded ? 'unset' : undefined },
                        '& td': { py: 1.5 },
                      }}
                      onClick={() => patrolActions.handlePatrolClick(patrol)}
                    >
                      <TableCell>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            bgcolor: status.bgColor,
                            color: status.color,
                            fontWeight: 600,
                            borderRadius: 1,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(patrol.startTime)} - {formatDateTime(patrol.endTime).split(', ')[1]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {patrol.guardName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {patrol.comment}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {isExpanded ? (
                            <KeyboardArrowDownIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          ) : (
                            <KeyboardArrowRightIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                          )}
                          {patrol.patrolType === 'Static' ? (
                            <Tooltip title="Static Patrol">
                              <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Mobile Patrol">
                              <EmojiTransportationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </Tooltip>
                          )}
                          {patrol.hasChecklist && (
                            <Tooltip title="Has Checklist">
                              <AssignmentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </Tooltip>
                          )}
                          {patrol.hasIncident && (
                            <Tooltip title="Has Incident">
                              <ReportProblemIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                            </Tooltip>
                          )}
                          {patrol.hasAttachment && (
                            <Tooltip title="Has Attachments">
                              <AttachFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Expandable Details Row */}
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <PatrolExpandedRow
                            patrolData={patrolData}
                            readOnly={readOnly}
                            onRefetch={onRefetch}
                            onViewFile={fileActions.handleViewFile}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20]}
          component="div"
          count={sortedPatrols.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Edit Patrol Modal */}
      <PatrolEditModal
        open={patrolActions.editModalOpen}
        onClose={() => patrolActions.setEditModalOpen(false)}
        selectedPatrol={patrolActions.selectedPatrol}
        patrolEditData={patrolActions.patrolEditData}
        setPatrolEditData={patrolActions.setPatrolEditData}
        setHasEdited={patrolActions.setHasEdited}
        hasEdited={patrolActions.hasEdited}
        pendingCheckpointEdits={patrolActions.pendingCheckpointEdits}
        isLoadingModalDetails={patrolActions.isLoadingModalDetails}
        isSavingPatrol={patrolActions.isSavingPatrol}
        onSave={patrolActions.handleSavePatrol}
        expandedChecklistId={patrolActions.expandedChecklistId}
        onToggleChecklist={patrolActions.handleToggleChecklist}
        editingChecklistItemId={patrolActions.editingChecklistItemId}
        checklistItemEdit={patrolActions.checklistItemEdit}
        setChecklistItemEdit={patrolActions.setChecklistItemEdit}
        onEditChecklistItem={patrolActions.handleEditChecklistItem}
        onSaveChecklistItem={patrolActions.handleSaveChecklistItemInModal}
        onCancelChecklistItemEdit={patrolActions.handleCancelChecklistItemEdit}
        onViewFile={fileActions.handleViewFile}
        onDownloadFile={fileActions.handleDownloadFile}
        onDeleteFile={fileActions.handleDeleteFileClick}
        onUpload={fileActions.handleOpenUploadDialog}
        readOnly={readOnly}
      />

      {/* Filter Modal */}
      <FilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filterIncidents={filterIncidents}
        setFilterIncidents={setFilterIncidents}
        filterPatrols={filterPatrols}
        setFilterPatrols={setFilterPatrols}
      />

      {/* Email Modal */}
      <EmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        emailChips={emailChips}
        emailInput={emailInput}
        setEmailInput={setEmailInput}
        onEmailKeyDown={handleEmailKeyDown}
        onDeleteChip={(email) => setEmailChips(emailChips.filter((e) => e !== email))}
        onSend={handleSendEmail}
        isSending={isSendingEmail}
        isLoadingEmails={isLoadingEmails}
      />

      {/* File Viewer */}
      <FileViewerDialog
        open={fileActions.isFileViewerOpen}
        onClose={fileActions.handleCloseFileViewer}
        file={fileActions.viewingFile}
        onDownload={fileActions.handleDownloadFile}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={fileActions.isDeleteDialogOpen}
        onClose={() => {
          fileActions.setIsDeleteDialogOpen(false);
          fileActions.setFileToDelete(null);
        }}
        file={fileActions.fileToDelete}
        onConfirm={fileActions.handleConfirmDeleteFile}
        isDeleting={fileActions.isDeletingFile}
      />

      {/* Upload Dialog */}
      <UploadDialog
        open={fileActions.isUploadDialogOpen}
        onClose={fileActions.handleCloseUploadDialog}
        uppy={fileActions.uppy}
        theme={theme}
      />
    </>
  );
};

export default PatrolTable;
